import React, { useEffect, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { GoDotFill } from "react-icons/go";
import { useLocation, useNavigate } from "react-router-dom";
import { NavItem } from "../../Data/Navigation";
import "./navigation.styles.css";

interface NavigationBarProps {
  navItems: NavItem[];
  onMasterMenuChange?: (menuName: string) => void;
}

const NavigationMenu: React.FC<NavigationBarProps> = ({ navItems, onMasterMenuChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeMasterMenu, setActiveMasterMenu] = useState<NavItem | null>(() => {
    const saved = localStorage.getItem('activeMasterMenu');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [openDropdowns, setOpenDropdowns] = useState<string[]>(() => {
    const saved = localStorage.getItem('openDropdowns');
    return saved ? JSON.parse(saved) : [];
  });

  
  useEffect(() => {
    if (activeMasterMenu) {
      localStorage.setItem('activeMasterMenu', JSON.stringify(activeMasterMenu));
    }
  }, [activeMasterMenu]);

  useEffect(() => {
    if (openDropdowns.length) {
      localStorage.setItem('openDropdowns', JSON.stringify(openDropdowns));
    }
  }, [openDropdowns]);

  // Combined useEffect for handling navigation state
  useEffect(() => {
    // Find the master menu item that matches the current path
    const findMasterMenu = (items: NavItem[]): NavItem | null => {
      for (const item of items) {
        // Handle other routes
        if (location.pathname.startsWith(item.link) && item.link !== '') {
          return item;
        }

        // Check children recursively
        if (item.children?.length) {
          const found = item.children.some(child => 
            location.pathname.startsWith(child.link) && child.link !== ''
          );
          if (found) return item;
        }
      }
      return null;
    };

    // Find active menu and set state
    const activeMenu = findMasterMenu(navItems);
    if (activeMenu) {
      setActiveMasterMenu(activeMenu);

      // Find which dropdowns should be open based on current path
      const findOpenDropdowns = (items: NavItem[]): string[] => {
        const dropdowns: string[] = [];
        
        const checkPath = (item: NavItem, currentPath: string) => {
          if (isDropdownItem(item)) {
            const itemBasePath = item.link || item.label.toLowerCase();
            
            if (currentPath.includes(itemBasePath)) {
              dropdowns.push(item.label);
            }
            
            item.children.forEach(child => {
              checkPath(child, currentPath);
            });
          }
        };

        items.forEach(item => {
          checkPath(item, location.pathname);
        });

        return dropdowns;
      };

      const dropdownsToOpen = findOpenDropdowns(activeMenu.children);
      setOpenDropdowns(dropdownsToOpen);
    }
  }, [location.pathname, navItems]);

  // Move findActiveMenuItem outside useEffect
  const findActiveMenuItem = (items: NavItem[]): NavItem | null => {
    const matchDynamicPath = (itemPath: string, currentPath: string) => {
        // Replace :id with actual value for comparison
        const dynamicPathPattern = itemPath.replace(/:id/g, '[^/]+');
        const regExp = new RegExp(`^${dynamicPathPattern}$`);
        return regExp.test(currentPath);
    };

    for (const item of items) {
        // Check exact match or dynamic route match
        if (item.link === location.pathname || matchDynamicPath(item.link, location.pathname)) {
            return item;
        }

        // Check if current path starts with item's path (for parent routes)
        if (location.pathname.startsWith(item.link) && item.link !== '') {
            return item;
        }

        // Check children
        if (item.children?.length) {
            const activeChild = findActiveMenuItem(item.children);
            if (activeChild) {
                return item;
            }
        }
    }
    return null;
  };

// Simplified path matcher
const matchPath = (path: string, pattern: string): boolean => {
  if (path === pattern) return true;
  
  // Handle dynamic routes
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');
  
  if (patternParts.length !== pathParts.length) return false;
  
  return patternParts.every((part, i) => 
    part.startsWith(':') || part === pathParts[i]
  );
};

  // Function to check if a link matches the current path or is a parent of current path
  const isActive = (item: NavItem, isSubmenu: boolean = false): boolean => {
    // For master menu items, only check if it's the active master menu
    if (!isSubmenu) {
      return activeMasterMenu?.label === item.label;
    }

    // For submenu items, use the existing logic
    const activeRoute = navItems
      .flatMap(getAllNavItems)
      .find(navItem => matchPath(location.pathname, navItem.link));
      
    if (activeRoute?.activeMenuItem === item.link) {
      return true;
    }
  
    if (matchPath(location.pathname, item.link)) {
      return true;
    }
  
    if (isDropdownItem(item)) {
      return item.children.some(child => isActive(child, true));
    }
  
    return false;
  };

  // Helper function to flatten all nav items recursively
const getAllNavItems = (item: NavItem): NavItem[] => {
  return [item, ...item.children.flatMap(getAllNavItems)];
};

  const isDropdownItem = (item: NavItem): boolean => {
    return Boolean(item.children && item.children.length > 0);
  };

  // Add a new function to check if it's a first-level menu item
  const isFirstLevelItem = (item: NavItem): boolean => {
    return Boolean(activeMasterMenu?.children?.includes(item));
  };

  const renderMasterMenu = (items: NavItem[]) => {
    return (
      <ul className="master-menu">
        {items
          .filter((item) => !item.isHidden)
          .map((item) => (
            <li
              key={`${item.label}-${item.link}`}
              className={`${isActive(item) ? "active" : ""}`}
              onClick={() => {
                handleMasterMenuClick(item);
              }}
            >
              <div className="menu-item " title={item.label}>
                <div className="icon-container">
                  {item.icon && (
                    <>
                      <img
                        src={require(`../../assets${item.icon}`)}
                        alt={item.label}
                        className="default-icon"
                      />
                      <img
                        src={require(`../../assets${item.iconOnHover}`)}
                        alt={item.label}
                        className="hover-icon"
                      />
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
      </ul>
    );
  };

  const renderSubMenu = (items: NavItem[]) => {
    return (
      <ul className={`submenu ${openDropdowns.length > 0 ? "open" : ""}`}>
        {items
          .filter((item) => !item.isHidden)
          .map((item) => (
            <li
              key={`${item.label}-${item.link}`}
              className={`${isActive(item, true) && !isDropdownItem(item) ? "active" : ""} ${
                isDropdownItem(item) ? "has-children" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (isDropdownItem(item)) {
                  toggleDropdown(item.label);
                } else if (item.link) {
                  const parentMenu = findActiveMenuItem(navItems);
                  if (parentMenu) {
                    setActiveMasterMenu(parentMenu);
                  }
                  navigate(item.link);
                }
              }}
            >
              <div className="menu-item w-100 ps-1 pe-2">
                {isDropdownItem(item) ? (
                  <span className="chevron">
                    {openDropdowns.includes(item.label) ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                ) : isFirstLevelItem(item) ? (
                  <span className="dot-icon">
                    <GoDotFill size={15} />
                  </span>
                ) : null}
                <span className="menu-label">{item.label}</span>
              </div>
              {isDropdownItem(item) && item.children?.length > 0 && (
                <div
                  className={`submenu-wrapper w-100 ${
                    openDropdowns.includes(item.label) ? "open" : ""
                  }`}
                >
                  {renderSubMenu(item.children)}
                </div>
              )}
            </li>
          ))}
      </ul>
    );
  };

  const handleMasterMenuClick = (item: NavItem) => {
    // Set the active master menu immediately
    setActiveMasterMenu(item);
    
    if (onMasterMenuChange) {
      onMasterMenuChange(item.label);
    }
  
    // If item has a link, navigate to it
    if (item.link) {
      navigate(item.link);
    }
    // If item has children but no link, navigate to first child's link
    else if (item.children && item.children.length > 0) {
      const firstChild = item.children.find(child => !child.isHidden);
      if (firstChild?.link) {
        navigate(firstChild.link);
      }
    }
  };

  // Function to handle dropdown toggle
  const toggleDropdown = (label: string) => {
    setOpenDropdowns((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  return (
    <div className="navigation-menu h-100">
      <div className="master-menu-container me-2">{renderMasterMenu(navItems)}</div>
      {activeMasterMenu && activeMasterMenu.children && activeMasterMenu.children.length > 0 && (
        <div className={`submenu-container d-flex flex-column ? 'collapsed' : ''}`}>
          <>
            <div className="">
              <div className="master-menu-title ps-1">
                <h6 className="active-master-menu-label">{activeMasterMenu.label}</h6>
              </div>
              {renderSubMenu(activeMasterMenu.children)}
            </div>
          </>
        </div>
      )}
    </div>
  );
};

export default NavigationMenu;
